/** Couleurs distinctes pour superposer les zones sur la carte (départements FR). */
export const ZONE_MAP_COLORS = [
  '#2563eb',
  '#dc2626',
  '#16a34a',
  '#ca8a04',
  '#9333ea',
  '#0891b2',
  '#ea580c',
  '#4f46e5',
  '#db2777',
  '#65a30d',
];

/** Pastilles légende (alignées sur ZONE_MAP_COLORS, sans style inline). */
export const ZONE_MAP_LEGEND_SWATCHES = [
  'bg-[#2563eb]',
  'bg-[#dc2626]',
  'bg-[#16a34a]',
  'bg-[#ca8a04]',
  'bg-[#9333ea]',
  'bg-[#0891b2]',
  'bg-[#ea580c]',
  'bg-[#4f46e5]',
  'bg-[#db2777]',
  'bg-[#65a30d]',
];

const FR_COUNTRY_HINTS = ['fr', 'fra', 'france'];
const MC_COUNTRY_HINTS = ['mc', 'mco', 'monaco'];

/** Identifiant carte pour Monaco (CP 98000 — pas un département INSEE). */
export const MONACO_AREA_CODE = 'MC';

/** Centre [lng, lat] pour marqueur SVG (polygone séparé = artefacts de remplissage avec geoPath). */
export const MONACO_CENTER_LNG_LAT = [
  (7.4093 + 7.442) / 2,
  (43.7235 + 43.7515) / 2,
];

/**
 * Extrait un CP français à 5 chiffres depuis la saisie (ex. "75 001", "Paris 75001", "75001").
 * La carte colore les départements uniquement à partir de ces CP.
 */
export function normalizeFrenchPostalInput(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const boundary = s.match(/\b(\d{5})\b/);
  if (boundary) return boundary[1];
  const digits = s.replace(/\D/g, '');
  if (digits.length >= 5) return digits.slice(0, 5);
  return null;
}

/** La zone concerne la France (codes postaux interprétables). */
export function isFranceServiceZone(zone) {
  const countries = (zone.countries || []).map(c =>
    String(c).trim().toLowerCase()
  );
  if (!countries.length) return true;
  return countries.some(c =>
    FR_COUNTRY_HINTS.some(h => c === h || c.includes('france'))
  );
}

/** France métro + Monaco (CP 98000 ou pays Monaco). */
export function isMappableFranceMapZone(zone) {
  if (isFranceServiceZone(zone)) return true;
  const countries = (zone.countries || []).map(c =>
    String(c).trim().toLowerCase()
  );
  if (
    countries.some(c =>
      MC_COUNTRY_HINTS.some(h => c === h || c.includes('monaco'))
    )
  ) {
    return true;
  }
  return (zone.postal_codes || []).some(pc => {
    return normalizeFrenchPostalInput(pc) === '98000';
  });
}

/**
 * Code département INSEE (ex. "75", "2A") à partir d'un code postal français à 5 chiffres.
 * DOM (971–976) : code à 3 caractères (non dessinés sur le GeoJSON métropolitain).
 */
export function frenchPostalToDepartmentCode(raw) {
  const cp = normalizeFrenchPostalInput(raw);
  if (!cp) return null;

  if (cp.startsWith('97') || cp.startsWith('98')) {
    const d3 = cp.slice(0, 3);
    if (['971', '972', '973', '974', '976'].includes(d3)) return d3;
    if (cp === '98000') return MONACO_AREA_CODE;
    return null;
  }

  if (cp.startsWith('20')) {
    const n = parseInt(cp, 10);
    return n < 20200 ? '2A' : '2B';
  }

  const d2 = cp.slice(0, 2);
  const di = parseInt(d2, 10);
  if (di >= 1 && di <= 95) return d2;
  return null;
}

/**
 * Pour chaque code département, la première zone (ordre du tableau) qui le revendique gagne.
 * @returns {Map<string, { zoneName: string; color: string }>}
 */
export function departmentAssignmentByPostal(zones) {
  const assignment = new Map();
  zones.forEach((zone, zIndex) => {
    if (!isMappableFranceMapZone(zone)) return;
    const color = ZONE_MAP_COLORS[zIndex % ZONE_MAP_COLORS.length];
    (zone.postal_codes || []).forEach(pc => {
      const dept = frenchPostalToDepartmentCode(pc);
      if (dept && !assignment.has(dept)) {
        assignment.set(dept, { zoneName: zone.name, color });
      }
    });
  });
  return assignment;
}

/** Style type FranceMap (react-simple-maps) : fond clair, bordure slate. */
const DEFAULT_GEO_FILL = '#f8fafc';
const DEFAULT_GEO_STROKE = '#cbd5e1';

function mapPaintForAssignment(assignment, code) {
  const z = assignment.get(code);
  if (z) {
    return {
      mapFill: z.color,
      mapFillOpacity: 0.78,
      mapStroke: DEFAULT_GEO_STROKE,
      zoneLabel: z.zoneName,
    };
  }
  return {
    mapFill: DEFAULT_GEO_FILL,
    mapFillOpacity: 1,
    mapStroke: DEFAULT_GEO_STROKE,
    zoneLabel: '',
  };
}

/**
 * Départements métro uniquement (Monaco rendu à part en SVG : cercle projeté).
 */
export function buildPaintedZonesFeatureCollection(
  departmentsCollection,
  zones
) {
  const assignment = departmentAssignmentByPostal(zones);

  return {
    type: 'FeatureCollection',
    features: departmentsCollection.features.map(f => {
      const code = String(f.properties.code);
      const paint = mapPaintForAssignment(assignment, code);
      return {
        ...f,
        properties: {
          ...f.properties,
          ...paint,
        },
      };
    }),
  };
}

/** Style carte pour Monaco (CP 98000), même logique que les départements. */
export function getMonacoMapPaint(zones) {
  const assignment = departmentAssignmentByPostal(zones);
  return mapPaintForAssignment(assignment, MONACO_AREA_CODE);
}
